import { RADIO_STATIONS } from '../../audio/radioStations';
import { radioStatusText } from '../../audio/radioStatus';
import {
  PALETTE_LABELS,
  SHADOW_LABELS,
  SNAP_TURN_CHOICES,
  TEXT_LABELS,
  soundFromSlider,
} from '../../ui/settings';
import type { ShellStore } from '../../ui/shell';
import type { TabletItem } from './tablet';
import { wrapWords } from './wrapWords';

export type ImmersivePage = 'closed' | 'menu' | 'settings' | 'sound' | 'quit';
export interface ImmersiveActions {
  navigate(page: ImmersivePage): void;
  rules(): void;
  reset(): void;
}

/** Real tablet hit targets, shared with the controller tests; no DOM dialogs inside XR. */
export function immersiveMenuItems(
  shell: ShellStore,
  page: ImmersivePage,
  actions: ImmersiveActions,
  scale = 1,
): TabletItem[] {
  const home = !shell.getSnapshot().playing;
  const items: TabletItem[] = [];
  const title =
    page === 'quit'
      ? 'Quit Game?'
      : page === 'settings'
        ? 'Settings'
        : page === 'sound'
          ? 'Radio & Sound'
          : home
            ? 'RPG-Arena'
            : 'Menu';
  items.push({ kind: 'text', text: title, x: 78, y: 105, size: 46 * scale });
  const button = (
    id: string,
    label: string,
    row: number,
    column: number,
    onTap: () => void,
    active = false,
  ) => {
    items.push({
      kind: 'gem',
      id,
      label,
      gem: 'diamond',
      enabled: true,
      active,
      rect: { x: 78 + column * 451, y: 140 + row * 84, w: 433, h: 68 },
      fontSize: 34 * scale,
      onTap,
    });
  };
  const back = () => actions.navigate('menu');
  if (page === 'quit') {
    items.push({
      kind: 'text',
      text: 'Leave this game? Unsaved progress is lost.',
      x: 78,
      y: 230,
      size: 34 * scale,
      maxWidth: 884,
    });
    button('quit-confirm', 'Quit Game', 3, 0, () => {
      actions.navigate('closed');
      shell.onQuitGame?.();
    });
    button('quit-cancel', 'Cancel', 3, 1, back);
    return items;
  }
  const store = shell.settings;
  const s = store?.get();
  if (page === 'settings' && store && s) {
    button('settings-sound', 'Radio & Sound', 0, 0, () => actions.navigate('sound'));
    button('settings-fog', `Smoke: ${s.fog ? 'On' : 'Off'}`, 0, 1, () =>
      store.set({ fog: !s.fog }),
    );
    button('settings-hints', `Hints: ${s.threatHints ? 'On' : 'Off'}`, 1, 0, () =>
      store.set({ threatHints: !s.threatHints }),
    );
    button('settings-text', `Text: ${TEXT_LABELS[s.tabletText]}`, 1, 1, () =>
      store.set({ tabletText: s.tabletText === 'normal' ? 'large' : 'normal' }),
    );
    button('settings-height-down', 'Seat height −', 2, 0, () =>
      store.set({
        comfort: { ...s.comfort, seatedHeight: Math.max(-0.5, s.comfort.seatedHeight - 0.1) },
      }),
    );
    button('settings-height-up', `Seat + (${s.comfort.seatedHeight.toFixed(1)} m)`, 2, 1, () =>
      store.set({
        comfort: { ...s.comfort, seatedHeight: Math.min(0.5, s.comfort.seatedHeight + 0.1) },
      }),
    );
    button('settings-palette', `Colours: ${PALETTE_LABELS[s.palette]}`, 3, 0, () =>
      store.set({ palette: s.palette === 'classic' ? 'colour-blind' : 'classic' }),
    );
    // The headset never draws shadows above Low (`capShadowQuality`): here they are Off or Low,
    // not four levels of which three look the same (client-tablets-ui:quality#20).
    const shadows = s.graphics.shadows === 'off' ? 'off' : 'low';
    button('settings-shadows', `Shadows: ${SHADOW_LABELS[shadows]}`, 3, 1, () =>
      store.set({ graphics: { ...s.graphics, shadows: shadows === 'off' ? 'low' : 'off' } }),
    );
    // No AI level here: a game keeps the one it was set up with (the owner, 2026-09-26).
    button('settings-guide', `Guide: ${s.guide ? 'On' : 'Off'}`, 4, 0, () =>
      store.set({ guide: !s.guide }),
    );
    // How the right stick turns: a step of 30, 45 or 90 degrees, or free (Settings, Turning).
    const turning = s.comfort.freeTurning ? 'Free' : `${s.comfort.snapTurnDegrees}°`;
    button('settings-turning', `Turning: ${turning}`, 4, 1, () => {
      const steps = SNAP_TURN_CHOICES;
      const at = s.comfort.freeTurning ? -1 : steps.indexOf(s.comfort.snapTurnDegrees);
      store.set({
        comfort:
          at === steps.length - 1
            ? { ...s.comfort, freeTurning: true }
            : { ...s.comfort, freeTurning: false, snapTurnDegrees: steps[at + 1]! },
      });
    });
    button('settings-back', 'Back', 5, 0, back);
    return items;
  }
  const radio = shell.radio;
  if (page === 'sound' && store && s && radio) {
    const r = radio.get();
    const choices = [...RADIO_STATIONS, { id: null, label: 'OFF' }];
    choices.forEach((station, i) =>
      button(
        `radio-${station.id ?? 'off'}`,
        station.label,
        Math.floor(i / 2),
        i % 2,
        () => {
          void radio.select(station.id);
        },
        r.selected === station.id,
      ),
    );
    const sound = s.sound.muted ? 0 : s.sound.volume;
    button('music-down', `Music − (${Math.round(r.volume * 100)}%)`, 3, 0, () =>
      radio.setVolume(r.volume - 0.1),
    );
    button('music-up', 'Music +', 3, 1, () => radio.setVolume(r.volume + 0.1));
    button('sound-down', `Sound − (${Math.round(sound * 100)}%)`, 4, 0, () =>
      store.set({ sound: soundFromSlider(sound - 0.1) }),
    );
    button('sound-up', 'Sound +', 4, 1, () => store.set({ sound: soundFromSlider(sound + 0.1) }));
    button('sound-back', 'Back', 5, 0, () => actions.navigate('settings'));
    // Beside Back, on two lines where it needs them rather than squeezed into one.
    const status = radioStatusText(r, r.selected !== null && radio.isOutOfOrder(r.selected));
    const lines = wrapWords(status, Math.floor(25 / scale), 2);
    lines.forEach((text, i) =>
      items.push({
        kind: 'text',
        text,
        x: 535,
        y: lines.length === 1 ? 605 : 590 + i * 40 * scale,
        size: 34 * scale,
        maxWidth: 427,
      }),
    );
    return items;
  }
  button('menu-settings', 'Settings', 0, 0, () => actions.navigate('settings'));
  button('menu-reset', 'Reset View', 0, 1, actions.reset);
  button('menu-rules', 'Rules', 1, 0, actions.rules);
  if (home) {
    button('home-new-game', 'New Game', 2, 0, () => {
      actions.navigate('closed');
      shell.onNewGame?.(shell.getSnapshot().setup);
    });
    items.push({
      kind: 'text',
      text: 'Starts the selected game and player setup.',
      x: 78,
      y: 455,
      size: 34 * scale,
      maxWidth: 884,
    });
  } else {
    button('menu-quit', 'Quit Game', 2, 0, () => actions.navigate('quit'));
    button('menu-back', 'Back', 2, 1, () => actions.navigate('closed'));
  }
  return items;
}
